import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PhotoIcon,
  LinkIcon,
  TagIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  FunnelIcon,
  PlusIcon,
  TrashIcon,
  StarIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import { catalogApi, productsApi } from '@/services/api';
import AiGenerateButton from '@/components/AiGenerateButton';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '@/components/common';
import CatalogSiteEditor from '@/components/cms/CatalogSiteEditor';
import CatalogSwitcher from '@/components/catalog/CatalogSwitcher';
import ProductSheetFields, { emptySheet, ProductSheet } from '@/components/catalog/ProductSheetFields';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface CatalogImage {
  url: string;
  alt: string;
  is_primary?: boolean;
}

interface CatalogProduct {
  _id: string;
  name: string;
  slug: string;
  short_description: string;
  description: string;
  category: string;
  subcategory?: string;
  brand: string;
  images: CatalogImage[];
  featured: boolean;
  catalog_active: boolean;
  attributes: Record<string, string>;
  tags: string[];
  external_url: string;
  cta_text: string;
  seo: {
    meta_title: string;
    meta_description: string;
    meta_keywords: string[];
    canonical_url?: string;
  };
  marketing: {
    landing_url?: string;
    campaign_tags?: string[];
    highlight_text?: string;
    badge_text?: string;
    social_share_text?: string;
  };
  status: 'active' | 'draft' | 'inactive';
  catalog_ids?: string[];
  sheet?: Partial<ProductSheet>;
  updated_at: string;
}

interface CatalogStats {
  total: number;
  active: number;
  draft: number;
  inactive: number;
  featured: number;
  categories: number;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    draft: 'bg-yellow-100 text-yellow-700',
    inactive: 'bg-gray-100 text-gray-600',
  };
  const labels: Record<string, string> = {
    active: 'Activo',
    draft: 'Borrador',
    inactive: 'Inactivo',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.inactive}`}>
      {labels[status] || status}
    </span>
  );
}

function CatalogBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-vida-100 text-vida-700">
      <EyeIcon className="w-3 h-3" /> En catalogo
    </span>
  ) : (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      Oculto
    </span>
  );
}

// ---------------------------------------------------------------------------
// Create Product Modal
// ---------------------------------------------------------------------------

interface CreateProductModalProps {
  onClose: () => void;
  onCreated: () => void;
  categories: string[];
  catalogId: string | null;
}

function CreateProductModal({ onClose, onCreated, categories, catalogId }: CreateProductModalProps) {
  const [saving, setSaving] = useState(false);
  const [showSeo, setShowSeo] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: '',
    short_description: '',
    description: '',
    status: 'draft' as 'active' | 'draft' | 'inactive',
    seo_meta_title: '',
    seo_meta_description: '',
    seo_meta_keywords: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    const seoKeywords = form.seo_meta_keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    const payload = {
      name: form.name,
      category: form.category,
      short_description: form.short_description,
      description: form.description,
      status: form.status,
      ...(form.seo_meta_title || form.seo_meta_description || seoKeywords.length
        ? {
            seo: {
              meta_title: form.seo_meta_title || undefined,
              meta_description: form.seo_meta_description || undefined,
              meta_keywords: seoKeywords.length ? seoKeywords : undefined,
            },
          }
        : {}),
    };
    setSaving(true);
    try {
      await catalogApi.createProduct(payload, catalogId);
      toast.success('Enlace creado correctamente');
      onCreated();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al crear el enlace');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 pt-16">
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Crear nuevo enlace</h2>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                placeholder="Nombre del enlace"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              {categories.length > 0 ? (
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                >
                  <option value="">Sin categoria</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                  placeholder="Ej: Naturaleza"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion corta</label>
              <textarea
                value={form.short_description}
                onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                placeholder="Breve descripcion del enlace..."
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Descripcion completa</label>
                <AiGenerateButton
                  task="product-description"
                  getContext={() => `${form.name}\n\n${form.short_description}`}
                  emptyHint="Rellena primero el nombre o la descripción corta."
                  onResult={(text) => setForm((f) => ({ ...f, description: text }))}
                />
              </div>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={4}
                className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                placeholder="Descripcion detallada del enlace..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado inicial</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as typeof form.status }))}
                className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
              >
                <option value="draft">Borrador</option>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>

            {/* SEO (collapsible) */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowSeo(!showSeo)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
              >
                <span className="flex items-center gap-2">
                  <GlobeAltIcon className="w-4 h-4" />
                  SEO (opcional)
                </span>
                {showSeo ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </button>
              {showSeo && (
                <div className="p-4 space-y-3 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta titulo <span className="text-xs text-gray-400">({form.seo_meta_title.length}/70)</span>
                    </label>
                    <input
                      type="text"
                      maxLength={70}
                      value={form.seo_meta_title}
                      onChange={(e) => setForm((f) => ({ ...f, seo_meta_title: e.target.value }))}
                      className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                      placeholder="Titulo para buscadores"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Meta descripcion <span className="text-xs text-gray-400">({form.seo_meta_description.length}/160)</span>
                      </label>
                      <AiGenerateButton
                        task="meta-description"
                        getContext={() =>
                          `${form.name}\n\n${form.short_description}\n\n${form.description}`
                        }
                        onResult={(text) =>
                          setForm((f) => ({ ...f, seo_meta_description: text.slice(0, 160) }))
                        }
                      />
                    </div>
                    <textarea
                      maxLength={160}
                      rows={2}
                      value={form.seo_meta_description}
                      onChange={(e) => setForm((f) => ({ ...f, seo_meta_description: e.target.value }))}
                      className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                      placeholder="Descripcion para resultados de busqueda"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">Palabras clave</label>
                      <AiGenerateButton
                        task="keywords"
                        label="Sugerir con IA"
                        getContext={() =>
                          `${form.name}\n\n${form.short_description}\n\n${form.description}`
                        }
                        onResult={(text) => {
                          // Merge AI keywords (comma-separated) with whatever is typed,
                          // de-duplicated, keeping the comma-separated string format.
                          const incoming = text.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
                          const current = form.seo_meta_keywords
                            .split(',').map((t) => t.trim()).filter(Boolean);
                          const merged = Array.from(new Set([...current, ...incoming]));
                          setForm((f) => ({ ...f, seo_meta_keywords: merged.join(', ') }));
                        }}
                      />
                    </div>
                    <input
                      type="text"
                      value={form.seo_meta_keywords}
                      onChange={(e) => setForm((f) => ({ ...f, seo_meta_keywords: e.target.value }))}
                      className="w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
                      placeholder="turismo, naturaleza, granada (separadas por coma)"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-vida-600 rounded-lg hover:bg-vida-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving && <LoadingSpinner size="sm" />}
                Crear enlace
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product detail / edit modal
// ---------------------------------------------------------------------------

interface ProductDetailProps {
  product: CatalogProduct;
  onClose: () => void;
  canEdit: boolean;
  onSaved: (updated: CatalogProduct) => void;
  catalogId: string | null;
}

function ProductDetail({ product, onClose, canEdit, onSaved, catalogId }: ProductDetailProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'seo' | 'marketing' | 'images' | 'sheet'>('general');
  const [sheet, setSheet] = useState<ProductSheet>(() => emptySheet(product.sheet));
  const [saving, setSaving] = useState(false);
  // Local image state separated from the parent so image actions (upload,
  // primary, delete, removing orphan refs) can be persisted IMMEDIATELY via
  // a small PATCH without having to click the main Save button. The rest of
  // the form (text, SEO, marketing) still uses the existing Save flow.
  const [images, setImages] = useState<CatalogImage[]>(() => product.images || []);
  const [uploading, setUploading] = useState(false);
  const [imageBusy, setImageBusy] = useState<string | null>(null); // url currently being deleted/primary-set
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set()); // urls that 404'd
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState({
    short_description: product.short_description,
    external_url: product.external_url,
    cta_text: product.cta_text,
    seo_meta_title: product.seo?.meta_title || '',
    seo_meta_description: product.seo?.meta_description || '',
    seo_meta_keywords: (product.seo?.meta_keywords || []).join(', '),
    seo_canonical_url: product.seo?.canonical_url || '',
    marketing_landing_url: product.marketing?.landing_url || '',
    marketing_highlight_text: product.marketing?.highlight_text || '',
    marketing_badge_text: product.marketing?.badge_text || '',
    marketing_social_share_text: product.marketing?.social_share_text || '',
  });

  // Persist only the images array. Returns the updated product or null on error.
  const persistImages = async (newImages: CatalogImage[]): Promise<boolean> => {
    try {
      const res = await catalogApi.updateProduct(product._id, { images: newImages }, catalogId);
      onSaved(res.data.product);
      return true;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(e?.response?.data?.message || e?.response?.data?.error || 'No se pudieron guardar las imágenes');
      return false;
    }
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded: CatalogImage[] = [];
    for (const file of Array.from(files)) {
      try {
        const urlRes = await productsApi.getUploadUrl(product._id, file.name);
        const { uploadUrl, publicUrl } = urlRes.data as { uploadUrl: string; objectKey: string; publicUrl: string };
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!putRes.ok) {
          const t = await putRes.text().catch(() => '');
          throw new Error(`MinIO ${putRes.status}: ${t.slice(0, 120)}`);
        }
        uploaded.push({ url: publicUrl, alt: file.name.replace(/\.[^.]+$/, ''), is_primary: false });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'unknown';
        toast.error(`Error subiendo ${file.name}: ${msg}`);
      }
    }
    if (uploaded.length > 0) {
      const next = [...images, ...uploaded];
      // If nothing was primary before, mark the first uploaded as primary.
      if (!next.some((i) => i.is_primary)) next[0] = { ...next[0], is_primary: true };
      setImages(next);
      const ok = await persistImages(next);
      if (ok) toast.success(`${uploaded.length} imagen${uploaded.length === 1 ? '' : 'es'} subida${uploaded.length === 1 ? '' : 's'}`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const setPrimary = async (url: string) => {
    setImageBusy(url);
    const next = images.map((i) => ({ ...i, is_primary: i.url === url }));
    setImages(next);
    await persistImages(next);
    setImageBusy(null);
  };

  const deleteImage = async (url: string) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    setImageBusy(url);
    const wasPrimary = images.find((i) => i.url === url)?.is_primary;
    let next = images.filter((i) => i.url !== url);
    // If we removed the primary, promote the first remaining.
    if (wasPrimary && next.length > 0) next[0] = { ...next[0], is_primary: true };
    setImages(next);
    const ok = await persistImages(next);
    if (ok) toast.success('Imagen eliminada');
    setImageBusy(null);
  };

  const tabs = [
    { id: 'general' as const, label: 'General', icon: SparklesIcon },
    { id: 'images' as const, label: 'Imagenes', icon: PhotoIcon },
    { id: 'seo' as const, label: 'SEO / SEM', icon: GlobeAltIcon },
    { id: 'marketing' as const, label: 'Marketing', icon: TagIcon },
    { id: 'sheet' as const, label: 'Ficha', icon: ClipboardDocumentListIcon },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        short_description: form.short_description,
        external_url: form.external_url,
        cta_text: form.cta_text,
        seo: {
          meta_title: form.seo_meta_title,
          meta_description: form.seo_meta_description,
          canonical_url: form.seo_canonical_url || undefined,
          meta_keywords: form.seo_meta_keywords
            .split(',')
            .map((k) => k.trim())
            .filter(Boolean),
        },
        marketing: {
          landing_url: form.marketing_landing_url || undefined,
          highlight_text: form.marketing_highlight_text || undefined,
          badge_text: form.marketing_badge_text || undefined,
          social_share_text: form.marketing_social_share_text || undefined,
          campaign_tags: product.marketing?.campaign_tags || [],
        },
        sheet,
      };
      const res = await catalogApi.updateProduct(product._id, payload, catalogId);
      toast.success('Enlace actualizado correctamente');
      onSaved(res.data.product);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = (editable: boolean) =>
    `w-full rounded-lg border-gray-300 shadow-sm text-sm ${editable ? 'focus:ring-vida-500 focus:border-vida-500' : 'bg-gray-50'}`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 pt-16">
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                {product.images[0] && (
                  <img src={product.images[0].url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{product.name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusBadge status={product.status} />
                  <CatalogBadge active={product.catalog_active} />
                  <span className="text-xs text-gray-400">/{product.slug}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b px-6 bg-white">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-vida-500 text-vida-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                {/* Short description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion corta</label>
                  <textarea
                    readOnly={!canEdit}
                    value={form.short_description}
                    onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))}
                    rows={2}
                    className={fieldClass(canEdit)}
                  />
                </div>

                {/* Categoria, subcategoria y vendedor.
                    OJO: `brand` es el VENDEDOR («Vendido por» en la tienda), NO una
                    subcategoria. La subcategoria tiene su propio campo
                    (catalog_meta.subcategory), que antes no se pintaba. */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                    <input
                      readOnly
                      value={product.category}
                      className="w-full rounded-lg border-gray-300 shadow-sm text-sm bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subcategoria</label>
                    <input
                      readOnly
                      value={product.subcategory || ''}
                      className="w-full rounded-lg border-gray-300 shadow-sm text-sm bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
                    <input
                      readOnly
                      value={product.brand}
                      className="w-full rounded-lg border-gray-300 shadow-sm text-sm bg-gray-50"
                    />
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Etiquetas</label>
                  <div className="flex flex-wrap gap-2">
                    {(product.tags || []).map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        <TagIcon className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Attributes */}
                {product.attributes && Object.keys(product.attributes).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ficha tecnica</label>
                    <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                      {Object.entries(product.attributes).map(([key, value]) => (
                        <div key={key} className="flex px-4 py-3">
                          <span className="w-1/3 text-sm font-medium text-gray-600">{key}</span>
                          <span className="w-2/3 text-sm text-gray-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* External link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enlace externo / Landing</label>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly={!canEdit}
                      value={form.external_url}
                      onChange={(e) => setForm((f) => ({ ...f, external_url: e.target.value }))}
                      className={`flex-1 rounded-lg border-gray-300 shadow-sm text-sm ${canEdit ? 'focus:ring-vida-500 focus:border-vida-500' : 'bg-gray-50'}`}
                    />
                    {form.external_url && (
                      <a
                        href={form.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-vida-600 hover:bg-vida-50 rounded-lg"
                      >
                        <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>

                {/* CTA */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Texto del boton CTA</label>
                  <input
                    readOnly={!canEdit}
                    value={form.cta_text}
                    onChange={(e) => setForm((f) => ({ ...f, cta_text: e.target.value }))}
                    className={fieldClass(canEdit)}
                  />
                </div>
              </div>
            )}

            {activeTab === 'images' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Gestiona las imagenes del enlace para el catalogo visual.</p>
                  {uploading && (
                    <span className="inline-flex items-center gap-2 text-xs text-vida-600"><LoadingSpinner size="sm" /> Subiendo…</span>
                  )}
                </div>

                {/* Input file oculto, dispara handleImageUpload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files)}
                />

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {images.map((img) => {
                    const broken = brokenImages.has(img.url);
                    const busy = imageBusy === img.url;
                    return (
                      <div key={img.url}
                        className={`relative group rounded-xl overflow-hidden border-2 aspect-[16/10] ${img.is_primary ? 'border-vida-500' : 'border-gray-200'} ${busy ? 'opacity-60' : ''}`}>
                        {broken ? (
                          // Placeholder for orphan / 404 images
                          <div className="w-full h-full bg-amber-50 flex flex-col items-center justify-center p-2 text-center">
                            <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 mb-1" />
                            <p className="text-[11px] text-amber-800 font-medium leading-tight">Imagen no encontrada</p>
                            <p className="text-[10px] text-amber-700 truncate w-full mt-0.5">{img.url.split('/').pop()}</p>
                            {canEdit && (
                              <button
                                onClick={() => deleteImage(img.url)}
                                disabled={busy}
                                className="mt-1 text-[10px] underline text-amber-900 hover:text-amber-700">
                                Eliminar referencia
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <img
                              src={img.url}
                              alt={img.alt}
                              className="w-full h-full object-cover"
                              onError={() => setBrokenImages((prev) => new Set(prev).add(img.url))}
                            />
                            {/* Action buttons overlay (top-right) */}
                            {canEdit && (
                              <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => setPrimary(img.url)}
                                  disabled={busy || img.is_primary}
                                  title={img.is_primary ? 'Imagen principal' : 'Marcar como principal'}
                                  className={`p-1 rounded ${img.is_primary ? 'bg-yellow-400 text-white' : 'bg-white/90 text-gray-600 hover:text-yellow-500'} disabled:opacity-50`}>
                                  {img.is_primary
                                    ? <StarIconSolid className="h-4 w-4" />
                                    : <StarIcon className="h-4 w-4" />}
                                </button>
                                <button
                                  onClick={() => deleteImage(img.url)}
                                  disabled={busy}
                                  title="Eliminar"
                                  className="p-1 bg-white/90 text-red-500 rounded hover:bg-red-100 disabled:opacity-50">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            {img.is_primary && (
                              <span className="absolute top-1.5 left-1.5 bg-vida-500 text-white text-xs px-2 py-0.5 rounded-full">
                                Principal
                              </span>
                            )}
                            {img.alt && (
                              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                <p className="text-white text-xs truncate">{img.alt}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  {canEdit && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 aspect-[16/10] text-gray-400 hover:border-vida-400 hover:text-vida-500 transition-colors disabled:opacity-50">
                      <PhotoIcon className="w-8 h-8 mb-1" />
                      <span className="text-xs font-medium">Anadir imagen</span>
                    </button>
                  )}
                </div>

                {images.length === 0 && (
                  <p className="text-xs text-gray-400 text-center">Este enlace no tiene imágenes. Pulsa "Añadir imagen" para subir la primera.</p>
                )}
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="space-y-6">
                <p className="text-sm text-gray-500">Optimizacion para motores de busqueda y visibilidad online.</p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                  <input
                    readOnly={!canEdit}
                    value={form.seo_meta_title}
                    onChange={(e) => setForm((f) => ({ ...f, seo_meta_title: e.target.value }))}
                    className={fieldClass(canEdit)}
                  />
                  <p className="mt-1 text-xs text-gray-400">{form.seo_meta_title.length}/60 caracteres</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Meta Description</label>
                    {canEdit && (
                      <AiGenerateButton
                        task="meta-description"
                        getContext={() =>
                          `${product.name}\n\n${product.short_description || ''}\n\n${product.description || ''}`
                        }
                        onResult={(text) =>
                          setForm((f) => ({ ...f, seo_meta_description: text.slice(0, 160) }))
                        }
                      />
                    )}
                  </div>
                  <textarea
                    readOnly={!canEdit}
                    value={form.seo_meta_description}
                    onChange={(e) => setForm((f) => ({ ...f, seo_meta_description: e.target.value }))}
                    rows={3}
                    className={fieldClass(canEdit)}
                  />
                  <p className="mt-1 text-xs text-gray-400">{form.seo_meta_description.length}/160 caracteres</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Meta Keywords</label>
                    {canEdit && (
                      <AiGenerateButton
                        task="keywords"
                        label="Sugerir con IA"
                        getContext={() =>
                          `${product.name}\n\n${product.short_description || ''}\n\n${product.description || ''}`
                        }
                        onResult={(text) => {
                          const incoming = text.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
                          const current = form.seo_meta_keywords
                            .split(',').map((t) => t.trim()).filter(Boolean);
                          const merged = Array.from(new Set([...current, ...incoming]));
                          setForm((f) => ({ ...f, seo_meta_keywords: merged.join(', ') }));
                        }}
                      />
                    )}
                  </div>
                  <input
                    type="text"
                    readOnly={!canEdit}
                    value={form.seo_meta_keywords}
                    onChange={(e) => setForm((f) => ({ ...f, seo_meta_keywords: e.target.value }))}
                    className={fieldClass(canEdit)}
                    placeholder="turismo, naturaleza, granada (separadas por coma)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Canonica</label>
                  <input
                    readOnly={!canEdit}
                    value={form.seo_canonical_url}
                    onChange={(e) => setForm((f) => ({ ...f, seo_canonical_url: e.target.value }))}
                    placeholder={form.external_url}
                    className={fieldClass(canEdit)}
                  />
                </div>

                {/* SEO Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vista previa en Google</label>
                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <p className="text-blue-700 text-lg font-normal truncate">{form.seo_meta_title}</p>
                    <p className="text-green-700 text-sm truncate">{form.external_url}</p>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{form.seo_meta_description}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sheet' && <ProductSheetFields value={sheet} onChange={setSheet} canEdit={canEdit} />}

            {activeTab === 'marketing' && (
              <div className="space-y-6">
                <p className="text-sm text-gray-500">Configuracion de campanas, enlaces de landing y contenido promocional.</p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Landing de Campana</label>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly={!canEdit}
                      value={form.marketing_landing_url}
                      onChange={(e) => setForm((f) => ({ ...f, marketing_landing_url: e.target.value }))}
                      placeholder="https://..."
                      className={`flex-1 rounded-lg border-gray-300 shadow-sm text-sm ${canEdit ? 'focus:ring-vida-500 focus:border-vida-500' : 'bg-gray-50'}`}
                    />
                    {form.marketing_landing_url && (
                      <a
                        href={form.marketing_landing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-vida-600 hover:bg-vida-50 rounded-lg"
                      >
                        <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Texto destacado</label>
                    <input
                      readOnly={!canEdit}
                      value={form.marketing_highlight_text}
                      onChange={(e) => setForm((f) => ({ ...f, marketing_highlight_text: e.target.value }))}
                      placeholder="Ej: Territorio UNESCO"
                      className={fieldClass(canEdit)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge / Etiqueta visual</label>
                    <input
                      readOnly={!canEdit}
                      value={form.marketing_badge_text}
                      onChange={(e) => setForm((f) => ({ ...f, marketing_badge_text: e.target.value }))}
                      placeholder="Ej: Nuevo, Destacado"
                      className={fieldClass(canEdit)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags de Campana</label>
                  <div className="flex flex-wrap gap-2">
                    {(product.marketing?.campaign_tags || []).map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                        {tag}
                        {canEdit && <XMarkIcon className="w-3 h-3 cursor-pointer hover:text-purple-900" />}
                      </span>
                    ))}
                    {canEdit && (
                      <button className="inline-flex items-center px-3 py-1 border border-dashed border-purple-300 text-purple-500 rounded-full text-sm hover:bg-purple-50">
                        + Anadir
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Texto para redes sociales</label>
                    {canEdit && (
                      <AiGenerateButton
                        task="social-post"
                        label="Generar post con IA"
                        getContext={() =>
                          `${product.name}\n\n${product.short_description || ''}\n\n${product.description || ''}`
                        }
                        onResult={(text) =>
                          setForm((f) => ({ ...f, marketing_social_share_text: text }))
                        }
                      />
                    )}
                  </div>
                  <textarea
                    readOnly={!canEdit}
                    value={form.marketing_social_share_text}
                    onChange={(e) => setForm((f) => ({ ...f, marketing_social_share_text: e.target.value }))}
                    rows={3}
                    placeholder="Texto que se compartira en redes sociales..."
                    className={fieldClass(canEdit)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {canEdit && (
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-vida-600 rounded-lg hover:bg-vida-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving && <LoadingSpinner size="sm" />}
                Guardar cambios
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function CatalogPage() {
  const { hasPermission } = useAuthStore();

  // View mode: products list vs site config editor
  const [viewMode, setViewMode] = useState<'products' | 'site'>('products');
  // Catálogo con el que se trabaja (VIDA-370): null = el principal.
  const [catalogId, setCatalogId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todas');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft' | 'inactive'>('all');
  const [catalogFilter, setCatalogFilter] = useState<'all' | 'visible' | 'hidden'>('visible');

  // Data
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // UI state
  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canRead = hasPermission(PERMISSIONS.CATALOG_READ);
  const canEdit = hasPermission(PERMISSIONS.CATALOG_UPDATE);

  // Build filter object for API calls
  const buildFilters = useCallback(() => {
    const filters: Record<string, string | boolean> = {};
    if (search) filters.search = search;
    if (categoryFilter !== 'Todas') filters.category = categoryFilter;
    if (statusFilter !== 'all') filters.status = statusFilter;
    if (catalogFilter === 'visible') filters.catalog_active = true;
    if (catalogFilter === 'hidden') filters.catalog_active = false;
    return filters;
  }, [search, categoryFilter, statusFilter, catalogFilter]);

  const loadProducts = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const res = await catalogApi.getProducts(targetPage, 20, buildFilters(), catalogId);
      const data = res.data;
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setTotalPages(data.pages || 1);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al cargar los enlaces');
    } finally {
      setLoading(false);
    }
  }, [buildFilters, catalogId]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await catalogApi.getCategories(catalogId);
      setCategories(res.data.categories || []);
    } catch {
      // Non-critical, silent fail
    }
  }, [catalogId]);

  const loadStats = useCallback(async () => {
    try {
      const res = await catalogApi.stats(catalogId);
      setStats(res.data);
    } catch {
      // Non-critical, silent fail
    }
  }, [catalogId]);

  // Initial load
  useEffect(() => {
    if (!canRead) return;
    loadCategories();
    loadStats();
  }, [canRead, loadCategories, loadStats]);

  // Reload products when filters or page changes
  useEffect(() => {
    if (!canRead) return;
    loadProducts(page);
  }, [canRead, page, loadProducts]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, statusFilter, catalogFilter, catalogId]);

  // Al cambiar de catálogo, las categorías del anterior ya no valen.
  useEffect(() => {
    setCategoryFilter('Todas');
  }, [catalogId]);

  // Mete o saca un enlace del catálogo actual, sin tocar los demás catálogos.
  const toggleInCatalog = async (product: CatalogProduct) => {
    setTogglingId(product._id);
    try {
      const ref = catalogId ?? 'principal';
      const inCatalog = product.catalog_active;
      await catalogApi.setCatalogProducts(ref, inCatalog ? { remove: [product._id] } : { add: [product._id] });
      toast.success(inCatalog ? 'Quitado de este catálogo' : 'Añadido a este catálogo');
      const stillListed = catalogFilter === 'all';
      setProducts((prev) => (stillListed
        ? prev.map((p) => (p._id === product._id ? { ...p, catalog_active: !inCatalog } : p))
        : prev.filter((p) => p._id !== product._id)));
      if (!stillListed) setTotal((prev) => prev - 1);
      loadStats();
      loadCategories();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'No se ha podido cambiar el enlace de catálogo');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (productId: string, productName: string, otherCatalogs: number) => {
    const extra = otherCatalogs > 0 ? ` También desaparecerá de ${otherCatalogs} catálogo(s) adicional(es).` : '';
    if (!window.confirm(`¿Seguro que quieres eliminar "${productName}"? Esta accion no se puede deshacer.${extra}`)) return;
    setDeletingId(productId);
    try {
      await catalogApi.deleteProduct(productId);
      toast.success('Enlace eliminado correctamente');
      setProducts((prev) => prev.filter((p) => p._id !== productId));
      setTotal((prev) => prev - 1);
      loadStats();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al eliminar el enlace');
    } finally {
      setDeletingId(null);
    }
  };

  const handleProductSaved = (updated: CatalogProduct) => {
    setProducts((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
    loadStats();
  };

  const handleCreated = () => {
    loadProducts(1);
    loadStats();
  };

  if (!canRead) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="p-8 text-center">
            <h2 className="text-xl font-bold text-gray-900">Acceso denegado</h2>
            <p className="mt-2 text-gray-600">No tienes permisos para ver esta pagina.</p>
          </div>
        </div>
      </div>
    );
  }

  const categoryPills = ['Todas', ...categories];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catálogo</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestiona el contenido de marketing, SEO e imagenes de tus enlaces para el catalogo visual.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View mode tabs */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('products')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'products'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Enlaces
            </button>
            <button
              onClick={() => setViewMode('site')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'site'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Catalogo Web
            </button>
          </div>
          {viewMode === 'products' && canEdit && (
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Crear Enlace
            </button>
          )}
        </div>
      </div>

      {/* Site config editor */}
      <CatalogSwitcher value={catalogId} onChange={setCatalogId} canEdit={canEdit} />

      {viewMode === 'site' && <CatalogSiteEditor catalogId={catalogId} />}

      {/* Products view */}
      {viewMode === 'products' && <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total enlaces</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.total ?? '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Visibles en catalogo</p>
          <p className="text-2xl font-bold text-vida-600">{stats?.active ?? '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">En borrador</p>
          <p className="text-2xl font-bold text-yellow-600">{stats?.draft ?? '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Categorias</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.categories ?? '—'}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o etiqueta..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="draft">Borrador</option>
            <option value="inactive">Inactivo</option>
          </select>

          {/* Catalog visibility filter */}
          <select
            value={catalogFilter}
            onChange={(e) => setCatalogFilter(e.target.value as typeof catalogFilter)}
            className="rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500"
          >
            <option value="all">Visibilidad: Todas</option>
            <option value="visible">Visible en catalogo</option>
            <option value="hidden">Oculto</option>
          </select>

          <div className="flex items-center gap-1 text-sm text-gray-500">
            <FunnelIcon className="w-4 h-4" />
            {total} enlaces
          </div>
        </div>

        {/* Category pills */}
        {categoryPills.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {categoryPills.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  categoryFilter === cat
                    ? 'bg-vida-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Product list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((product) => {
            const isExpanded = expandedId === product._id;
            const primaryImg = product.images?.find((i) => i.is_primary) || product.images?.[0];
            const isDeleting = deletingId === product._id;

            return (
              <div key={product._id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                {/* Main row */}
                <div className="flex items-center gap-4 p-4">
                  {/* Image */}
                  <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    {primaryImg && (
                      <img src={primaryImg.url} alt={primaryImg.alt} className="w-full h-full object-cover" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                      <StatusBadge status={product.status} />
                      <CatalogBadge active={product.catalog_active} />
                      {product.marketing?.badge_text && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          {product.marketing.badge_text}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate mt-0.5">{product.short_description}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      {product.category && <span className="text-xs text-gray-400">{product.category}</span>}
                      {product.category && product.images?.length > 0 && <span className="text-xs text-gray-300">|</span>}
                      {product.images?.length > 0 && <span className="text-xs text-gray-400">{product.images.length} fotos</span>}
                      {product.tags?.length > 0 && (
                        <>
                          <span className="text-xs text-gray-300">|</span>
                          <span className="text-xs text-gray-400">{product.tags.length} tags</span>
                        </>
                      )}
                      {product.external_url && (
                        <>
                          <span className="text-xs text-gray-300">|</span>
                          <a href={product.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-vida-600 hover:text-vida-700">
                            <LinkIcon className="w-3 h-3" />
                            Landing
                          </a>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setSelectedProduct(product)}
                      className="p-2 text-gray-400 hover:text-vida-600 hover:bg-vida-50 rounded-lg transition-colors"
                      title={canEdit ? 'Editar enlace' : 'Ver enlace'}
                    >
                      <PencilSquareIcon className="w-5 h-5" />
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => toggleInCatalog(product)}
                        disabled={togglingId === product._id}
                        className="px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                        title={product.catalog_active ? 'Quitar de este catálogo (el enlace no se borra)' : 'Añadir a este catálogo'}
                      >
                        {product.catalog_active ? 'Quitar' : 'Añadir'}
                      </button>
                    )}
                    {/* Borrar elimina el enlace de todos los catálogos: solo desde el principal. */}
                    {canEdit && !catalogId && (
                      <button
                        onClick={() => handleDelete(product._id, product.name, product.catalog_ids?.length ?? 0)}
                        disabled={isDeleting}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Eliminar enlace"
                      >
                        {isDeleting ? <LoadingSpinner size="sm" /> : <TrashIcon className="w-5 h-5" />}
                      </button>
                    )}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : product._id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Expanded preview */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Images grid */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Imagenes</h4>
                        {product.images?.length > 0 ? (
                          <div className="grid grid-cols-2 gap-2">
                            {product.images.map((img, idx) => (
                              <div key={idx} className="rounded-lg overflow-hidden aspect-video bg-gray-200">
                                <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">Sin imagenes</p>
                        )}
                      </div>

                      {/* SEO preview */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">SEO</h4>
                        {product.seo ? (
                          <>
                            <div className="bg-white rounded-lg p-3 border border-gray-200 space-y-1">
                              <p className="text-blue-700 text-sm truncate">{product.seo.meta_title}</p>
                              <p className="text-green-700 text-xs truncate">{product.external_url}</p>
                              <p className="text-gray-600 text-xs line-clamp-2">{product.seo.meta_description}</p>
                            </div>
                            {product.seo.meta_keywords?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {product.seo.meta_keywords.map((kw) => (
                                  <span key={kw} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{kw}</span>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-gray-400">Sin datos SEO</p>
                        )}
                      </div>

                      {/* Marketing info */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Marketing</h4>
                        <div className="space-y-2 text-sm">
                          {product.marketing?.highlight_text && (
                            <div className="flex items-center gap-2">
                              <SparklesIcon className="w-4 h-4 text-amber-500" />
                              <span className="text-gray-700">{product.marketing.highlight_text}</span>
                            </div>
                          )}
                          {product.marketing?.landing_url && (
                            <div className="flex items-center gap-2">
                              <LinkIcon className="w-4 h-4 text-vida-500" />
                              <a href={product.marketing.landing_url} target="_blank" rel="noopener noreferrer" className="text-vida-600 hover:underline truncate">
                                {product.marketing.landing_url}
                              </a>
                            </div>
                          )}
                          {product.marketing?.campaign_tags && product.marketing.campaign_tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {product.marketing.campaign_tags.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">{tag}</span>
                              ))}
                            </div>
                          )}
                          {product.cta_text && (
                            <div className="flex items-center gap-2 pt-1">
                              <span className="text-xs text-gray-400">CTA:</span>
                              <span className="px-2 py-0.5 bg-vida-100 text-vida-700 rounded text-xs font-medium">{product.cta_text}</span>
                            </div>
                          )}
                          {!product.marketing?.highlight_text && !product.marketing?.landing_url && !product.cta_text && (
                            <p className="text-xs text-gray-400">Sin datos de marketing</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {products.length === 0 && !loading && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <MagnifyingGlassIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900">No se encontraron enlaces</h3>
              <p className="mt-1 text-sm text-gray-500">Prueba a cambiar los filtros de busqueda.</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="text-sm text-gray-500">
            Pagina {page} de {totalPages} — {total} enlaces en total
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (page <= 4) {
                pageNum = i + 1;
              } else if (page >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = page - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                    pageNum === page
                      ? 'bg-vida-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Detail / edit modal */}
      {selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          canEdit={canEdit}
          onSaved={handleProductSaved}
          catalogId={catalogId}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateProductModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          categories={categories}
          catalogId={catalogId}
        />
      )}
      </>}
    </div>
  );
}
